import { getUserDBWhitelistingAndAzureStatus } from '../http';
import CONSTANTS from '../contants';
import { Magic } from '../contexts/MagicContext';

class UserStatusMonitor {
  private intervalId: NodeJS.Timeout | null = null;
  private isMonitoring = false;
  private magicInstance: Magic | null = null;

  /**
   * Set Magic instance for logout functionality
   */
  setMagicInstance(magic: Magic | null) {
    this.magicInstance = magic;
  }

  /**
   * Start monitoring user status every 1 minute
   */
  startMonitoring() {
    if (this.isMonitoring) {
      return; // Already monitoring
    }

    this.isMonitoring = true;
    
    // Check immediately when starting
    this.checkUserStatus();
    
    // Set up interval for every 1 minute (60000ms)
    this.intervalId = setInterval(() => {
      this.checkUserStatus();
    }, 10000);

  }

  /**
   * Stop monitoring user status
   */
  stopMonitoring() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isMonitoring = false;
    console.log('User status monitoring stopped');
  }

  /**
   * Check user status and clear storage if needed
   */
  private async checkUserStatus() {
    try {
      const token = localStorage.getItem(CONSTANTS.token);
      
      // Only check if user is logged in
      if (!token) {
        this.stopMonitoring();
        return;
      }

      const response = await getUserDBWhitelistingAndAzureStatus();
      const { userDBWhitelistingStatus, userAzureStatus,userDomainWhitelistStatus } = response.data;

      // Get previously stored statuses
      const storedDBStatus = localStorage.getItem('userDBWhitelistingStatus');
      const storedAzureStatus = localStorage.getItem('userAzureStatus');
      const storedDomainWhitelistStatus = localStorage.getItem('userDomainWhitelistStatus');
      // If this is the first API call, save the statuses
      if (storedDBStatus === null || storedAzureStatus === null || storedDomainWhitelistStatus === null ) {
        localStorage.setItem('userDBWhitelistingStatus', userDBWhitelistingStatus.toString());
        localStorage.setItem('userAzureStatus', userAzureStatus.toString());
        localStorage.setItem('userDomainWhitelistStatus', userDomainWhitelistStatus.toString());

        console.log('Initial user statuses saved to localStorage');
        return;
      }

      // Check if any status changed from true to false
      const dbStatusChanged = storedDBStatus === 'true' && !userDBWhitelistingStatus ;
      const azureStatusChanged = storedAzureStatus === 'true' && !userAzureStatus;
      const domainWhitelistStatusChanged = storedDomainWhitelistStatus === 'true' && !userDomainWhitelistStatus;
      if (dbStatusChanged || azureStatusChanged || domainWhitelistStatusChanged) {
        console.warn('User status changed from true to false - clearing storage');
        this.clearStorageAndLogout();
      } else {
        // Update stored statuses with current values
        localStorage.setItem('userDBWhitelistingStatus', userDBWhitelistingStatus.toString());
        localStorage.setItem('userAzureStatus', userAzureStatus.toString());
        localStorage.setItem('userDomainWhitelistStatus', userDomainWhitelistStatus.toString());
      }
    } catch (error) {
      console.error('Error checking user status:', error);
      // On error, we don't clear storage to avoid false positives
      // The existing 401 interceptor will handle authentication errors
    }
  }

  /**
   * Clear localStorage, sessionStorage and redirect to login
   */
  private async clearStorageAndLogout() {
    try {
      // Logout from Magic first to clear the session
      if (this.magicInstance) {
        await this.magicInstance.user.logout();
        console.log('Magic logout successful');
      }
    } catch (error) {
      console.error('Error during Magic logout:', error);
    }

    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Stop monitoring
    this.stopMonitoring();
    
    // Redirect to login page
    const pathname = window.location.pathname;
    if (pathname !== '/' && !pathname.includes('compare-doc')) {
      window.location.pathname = '/';
    }
  }

  /**
   * Check if monitoring is active
   */
  isActive(): boolean {
    return this.isMonitoring;
  }

  /**
   * Manual logout function for testing
   */
  async manualLogout() {
    console.log('Manual logout triggered');
    await this.clearStorageAndLogout();
  }
}

// Create a singleton instance
const userStatusMonitor = new UserStatusMonitor();

export default userStatusMonitor;
